import React, { useEffect, useCallback, useRef, useId } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Editorial in-page lightbox.
 * Props:
 *   items: [{ src, caption, subcaption? }]
 *   index: current index (or null when closed)
 *   onClose, onPrev, onNext
 */
export default function Lightbox({ items, index, onClose, onPrev, onNext }) {
  const open = index !== null && index !== undefined && items[index];
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previouslyFocused = useRef(null);
  const titleId = useId();

  const onKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") return onClose();
      if (e.key === "ArrowLeft") return onPrev();
      if (e.key === "ArrowRight") return onNext();
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [open, onClose, onPrev, onNext]
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the close button on open so Tab/Shift-Tab cycles within the dialog.
    requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the element that opened the lightbox.
      if (previouslyFocused.current && typeof previouslyFocused.current.focus === "function") {
        previouslyFocused.current.focus();
      }
    };
  }, [open, onKey]);

  if (!open) return null;
  const item = items[index];
  const total = items.length;

  return (
    <div
      ref={dialogRef}
      data-testid="lightbox"
      className="fixed inset-0 z-[80] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={item.caption ? titleId : undefined}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        data-testid="lightbox-backdrop"
        className="absolute inset-0 bg-brand-navyDarker/95 backdrop-blur-md"
      />

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-6 md:px-10 py-5">
        <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 font-semibold">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label="Close"
          data-testid="lightbox-close"
          className="text-white/70 hover:text-white p-2 transition-colors"
        >
          <X size={22} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>

      {/* Image */}
      <div className="relative z-10 w-[92vw] md:w-[80vw] max-w-6xl max-h-[78vh] flex flex-col items-center">
        <img
          src={item.src}
          alt={item.caption || ""}
          className="max-h-[68vh] w-auto rounded-xl object-contain shadow-2xl"
        />
        <div className="mt-6 text-center max-w-2xl">
          {item.caption && (
            <h3 id={titleId} className="font-heading text-white text-xl md:text-2xl font-medium tracking-tight">
              {item.caption}
            </h3>
          )}
          {item.subcaption && (
            <p className="mt-2 text-sm text-white/55">{item.subcaption}</p>
          )}
        </div>
      </div>

      {/* Prev / Next */}
      {total > 1 && (
        <>
          <button
            onClick={onPrev}
            aria-label="Previous"
            data-testid="lightbox-prev"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.12] hover:border-white/40 text-white flex items-center justify-center transition-all"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={onNext}
            aria-label="Next"
            data-testid="lightbox-next"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.12] hover:border-white/40 text-white flex items-center justify-center transition-all"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}
    </div>
  );
}
